/**
 * 订单状态进度条（四节点步骤条 + 异常红色）
 * 对齐后端 model.TradeOrder（代码口径，P0-5 2026-08-20）：
 * 0-待付款 1-已付款 2-已发货 3-已完成 4-已取消 5-已退款 6-已过期 7-售后中
 *  - 已取消(4)/已退款(5)/已过期(6) → 异常态（红色展示）；
 *  - 售后中(7) → 保留 4 步进度（已到"已完成"）并在顶部展示金色售后提示；
 *  - settled=true（Completed + settled_at，代码 IsOrderSettled）→ 末步展示「已结算」金色标签。
 */
import { ORDER_PROGRESS, orderProgressIndex, orderStatusMeta } from '@/utils/constants';

interface StatusProgressProps {
  status: number;
  settled?: boolean;
}

/** 异常态元数据（4 已取消 / 5 已退款 / 6 已过期） */
const ABNORMAL: Record<number, { label: string; text: string }> = {
  4: { label: '已取消', text: '订单已取消，牛只已释放' },
  5: { label: '已退款', text: '订单已退款，资金已原路退回托管账户' },
  6: { label: '已过期', text: '订单已超时自动关闭（15 分钟未支付），牛只已释放' },
};

export default function StatusProgress({ status, settled }: StatusProgressProps) {
  const current = orderProgressIndex(status);

  // 异常终态：已取消 / 已退款 / 已过期（整条红色 + ×）
  if (ABNORMAL[status]) {
    const meta = ABNORMAL[status];
    return (
      <div className="status-progress status-progress--abnormal">
        <div className="status-progress__tip">
          <span className="status-tag status-tag--danger">{meta.label}</span>
          <span className="status-progress__tip-text">{meta.text}</span>
        </div>
        <div className="status-progress__bar">
          {ORDER_PROGRESS.map((label) => (
            <div className="status-progress__step abnormal" key={label}>
              <div className="status-progress__node">×</div>
              <div className="status-progress__label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 售后中：已完成交割后进入争议处理（进度到"已完成"，顶部金色提示）
  if (status === 7) {
    return (
      <div className="status-progress status-progress--aftersales">
        <div className="status-progress__tip">
          <span className="status-tag status-tag--gold">售后中</span>
          <span className="status-progress__tip-text">订单已完成交割，买家发起售后，处理完成前订单暂停结算/评价</span>
        </div>
        <div className="status-progress__bar">
          {ORDER_PROGRESS.map((label, i) => (
            <div className={['status-progress__step', i < 3 ? 'done' : '', i === 3 ? 'active' : ''].join(' ')} key={label}>
              <div className="status-progress__node">{i < 3 ? '✓' : i + 1}</div>
              <div className="status-progress__label">{label}</div>
            </div>
          ))}
        </div>
        <div className="status-progress__current">
          当前状态：
          <span className="status-tag status-tag--gold">{orderStatusMeta(7).label}</span>
        </div>
      </div>
    );
  }

  // 正常流转（含 Completed → 已结算的派生标记）
  const currentMeta = orderStatusMeta(status, settled);
  const currentIdx = current >= 0 ? current : 0;

  return (
    <div className="status-progress">
      <div className="status-progress__bar">
        {ORDER_PROGRESS.map((label, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div
              className={[
                'status-progress__step',
                done ? 'done' : '',
                active ? 'active' : '',
              ].join(' ')}
              key={label}
            >
              <div className="status-progress__node">{done ? '✓' : i + 1}</div>
              <div className="status-progress__label">{label}</div>
            </div>
          );
        })}
      </div>
      <div className="status-progress__current">
        当前状态：
        <span className={`status-tag status-tag--${currentMeta.tone}`}>
          {currentMeta.label}
        </span>
        {settled && status === 3 ? (
          <span className="status-progress__tip-text">已完成平台财务结算，款项/服务费已按托管规则处理</span>
        ) : null}
      </div>
    </div>
  );
}
